import { SelectionModel } from '@angular/cdk/collections';
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { GetIntegratedProtoReferral, GetIntegratedProtoReferralItem } from '@app/api-module/control-centre-api-client';
import { CommonUtilities } from '@app/shared/common-utilities';
import { Subscription } from 'rxjs';
import { firstBy } from 'thenby';

@Component({
    selector: 'app-patient-search-list',
    templateUrl: './patient-search-list.component.html',
    styleUrl: './patient-search-list.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class PatientSearchListComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() protoReferralList: GetIntegratedProtoReferral[] | null = [];

    @Output()
    clearSearch = new EventEmitter<void>();

    @Output()
    loadPatientData = new EventEmitter<{
        protoReferral: GetIntegratedProtoReferral;
        selectedItems: GetIntegratedProtoReferralItem[];
    }>();

    count = 0;

    @ViewChild(MatSort) protoReferralSort!: MatSort;

    readonly displayedPatientColumns: string[] = ['firstName', 'lastName', 'patientExternalId', 'dateOfBirth', 'siteName'];

    readonly displayedScanRegionsColumns: string[] = ['select', 'modality', 'bodyPart', 'phaseOfContrast'];

    private readonly _subscriptions = new Subscription();

    selectedProtoReferral: GetIntegratedProtoReferral | null;

    protoReferralSource = new MatTableDataSource<GetIntegratedProtoReferral>();

    scanRegionsSource = new MatTableDataSource<GetIntegratedProtoReferralItem>();
    scanRegionSelection = new SelectionModel<GetIntegratedProtoReferralItem>(true, []);
    allScanRegionsSelected = false;

    constructor() {
        this._subscriptions.add(
            this.scanRegionSelection.changed.subscribe(_ => (this.allScanRegionsSelected = this.isAllScanRegionsSelected()))
        );
    }

    ngAfterViewInit(): void {
        this.protoReferralSort.disableClear = true;
        this.protoReferralSource.sort = this.protoReferralSort;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.protoReferralList) {
            this.selectedProtoReferral = null;
            this.protoReferralSource.data = changes.protoReferralList.currentValue ?? [];
            this.protoReferralSource.sortData = this.sortPatientRows;
        }
    }

    cancelSearch() {
        this.clearSearch.emit();
    }

    onPatientRowClick(row: GetIntegratedProtoReferral) {
        this.scanRegionSelection.clear();
        if (this.selectedProtoReferral?.patientExternalId !== row.patientExternalId || this.selectedProtoReferral?.siteId !== row.siteId) {
            this.selectedProtoReferral = row;

            // update the scan regions
            this.scanRegionsSource.data = this.selectedProtoReferral.regionsForScanning;

            this.scrollSelectedRowIntoView();
        } else {
            // deselect
            this.selectedProtoReferral = null;
            this.scanRegionsSource.data = [];
        }
    }

    private scrollSelectedRowIntoView() {
        // the patient search table will shrink after we start displaying the scan regions for the selected one
        // so jump the scroll of the patient search table so that the selected item is in view
        setTimeout(() => {
            const selectedRow = document.getElementsByClassName('selectedRow').item(0);
            const patientSearchTableRect = document.getElementById('patient-search-table-wrapper').getBoundingClientRect();

            // this is to handle the user quickly selecting and then deselecting a row
            if (!selectedRow) {
                return;
            }

            const selectedRowRect = selectedRow.getBoundingClientRect();
            if (selectedRowRect.y <= 0 || selectedRowRect.y + selectedRowRect.height > patientSearchTableRect.bottom) {
                selectedRow.scrollIntoView({ block: 'nearest' });
            }
        }, 15);
    }

    sortPatientRows(rows: GetIntegratedProtoReferral[], sort: MatSort): GetIntegratedProtoReferral[] {
        const fieldName = sort.active;
        const isAsc = sort.direction === 'asc';

        return rows.sort(
            firstBy((left: GetIntegratedProtoReferral, right: GetIntegratedProtoReferral) => {
                if (fieldName === 'dateOfBirth') {
                    return CommonUtilities.compareStringDateWithAscOrDescOrder(left[fieldName], right[fieldName], isAsc);
                } else {
                    return CommonUtilities.compareStringWithAscOrDescOrder(left[fieldName] as string, right[fieldName] as string, isAsc);
                }
            })
        );
    }

    toggleAllScanRegionRows() {
        if (this.isAllScanRegionsSelected()) {
            this.scanRegionSelection.clear();
        } else {
            this.scanRegionsSource.data.forEach(row => this.scanRegionSelection.select(row));
        }
    }

    onLoadClick() {
        this.loadPatientData.emit({
            protoReferral: this.selectedProtoReferral,
            selectedItems: this.scanRegionSelection.selected
        });

        this.clearSearch.emit();
    }

    private isAllScanRegionsSelected() {
        const numSelected = this.scanRegionSelection.selected.length;
        const numRows = this.scanRegionsSource.data.length;
        return numSelected === numRows;
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribe();
    }
}
